import { motion } from "framer-motion";
import { Users, ShoppingCart, Plus } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { MenuSet } from "@/lib/index";
import { useCart } from "@/hooks/useCart";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";

/**
 * MenuCard Component
 * Displays a specific menu set (Ayam or Kambing) with pricing, image, and order CTA.
 */
export function MenuCard({
  menuSet
}: {
  menuSet: MenuSet;
}) {
  const {
    addItem
  } = useCart();
  const handleAddToCart = () => {
    addItem(menuSet, 'menu');
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} whileHover={{
    y: -8
  }} transition={{
    type: "spring",
    stiffness: 300,
    damping: 25
  }} className="h-full">
      <Card className="h-full flex flex-col overflow-hidden border-2 border-border/40 hover:border-primary/50 transition-colors bg-card shadow-sm">
        {/* Image Section */}
        <div className="relative aspect-[16/10] overflow-hidden group">
          
          
          
          {/* Pax Badge */}
          <div className="absolute bottom-3 left-3">
            <Badge className="bg-background/90 backdrop-blur-sm text-foreground border-none flex items-center gap-1.5 py-1 px-2.5 shadow-sm">
              <Users className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold">{menuSet.pax}</span>
            </Badge>
          </div>

          {/* Type Badge */}
          <div className="absolute top-3 left-3">
            <Badge className={menuSet.type === 'kambing' ? "bg-destructive/90 text-destructive-foreground border-none" : "bg-primary/90 text-primary-foreground border-none"}>
              {menuSet.type.toUpperCase()}
            </Badge>
          </div>
        </div>

        {/* Content Section */}
        <CardHeader className="pt-5 pb-2 px-5">
          <div className="flex justify-between items-start gap-2">
            <h3 className="text-xl font-bold tracking-tight text-foreground group-hover:text-primary transition-colors">
              {menuSet.name}
            </h3>
            <span className="font-mono text-lg font-bold text-primary whitespace-nowrap">
              RM {menuSet.price}
            </span>
          </div>
        </CardHeader>

        <CardContent className="px-5 flex-grow pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {menuSet.description}
          </p>
          
          {/* Set Contents Checklist - Simple & Reliable */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-1 h-1 rounded-full bg-primary" />
              <span>Aromatic Mandy Rice</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-1 h-1 rounded-full bg-primary" />
              <span>Fresh Salatah (Acar)</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-1 h-1 rounded-full bg-primary" />
              <span>Spicy Sahawiq (Sambal)</span>
            </div>
          </div>
        </CardContent>

        {/* Footer Section */}
        <CardFooter className="px-5 pb-5 pt-0">
          <Button onClick={handleAddToCart} className="w-full group/btn bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 rounded-xl transition-all active:scale-95 shadow-md hover:shadow-primary/20">
            <ShoppingCart className="w-4 h-4 mr-2 transition-transform group-hover/btn:scale-110" />
            Add to Cart
            <Plus className="w-4 h-4 ml-1 opacity-0 -translate-x-2 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all" />
          </Button>
        </CardFooter>
      </Card>
    </motion.div>;
}